import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { DownloadPayrollTableExport } from '../../../../api/Payroll/payrollReports';
import Button from '../../../../components/ui/Button';
import { saveBlob } from '../../../../components/reports/useReportExport';
import type { Department } from '../../../../models/general';
import type { MonthlyPayrollView } from '../../../../models/payroll';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import ColumnsSection from './ColumnsSection';
import EmployeePicker, { type ExportEmployee } from './EmployeePicker';
import LayoutSection from './LayoutSection';
import { exportFileName, loadOptions, needsGrouping, saveOptions, type ColumnAccess, type ExportOptions } from './exportOptions';
import { buildExportRequest } from './exportText';

export interface PayrollExportFormProps {
  /** Every payslip of the month: only these can be exported. */
  payslips: MonthlyPayrollView[];
  /** The rows the page's filters leave. */
  filtered: MonthlyPayrollView[];
  /** The rows ticked on the page. */
  tickedIds: Set<number>;
  departments: Department[];
  year: number;
  month: number;
  currencyId: number;
  currencyCode?: string;
  periodText: string;
}

interface Props extends PayrollExportFormProps {
  access: ColumnAccess;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onCancel: () => void;
  onDownloaded: (fileName: string) => void;
}

/**
 * The panel's content. It mounts when the panel opens, so the employee list starts from the page each
 * time while the remembered options come back from storage.
 */
const PayrollExportForm: React.FC<Props> = ({
  payslips, filtered, tickedIds, departments, year, month, currencyId, periodText,
  access, busy, setBusy, onCancel, onDownloaded,
}) => {
  const { t, i18n } = useTranslation();
  const [options, setOptions] = useState<ExportOptions>(() => loadOptions(access));
  const [error, setError] = useState<string | null>(null);

  // The page clears its ticks whenever a filter changes, so ticked rows are always inside the filters
  // today. The note names the ticks either way, and the id check keeps a stale tick out of the request.
  const [start] = useState(() => {
    const onMonth = new Set(payslips.map((p) => p.id));
    const ticked = Array.from(tickedIds).filter((id) => onMonth.has(id));
    return ticked.length
      ? { kind: 'ticked' as const, ids: ticked }
      : { kind: 'filters' as const, ids: filtered.map((p) => p.id) };
  });
  const [selected, setSelected] = useState<Set<number>>(() => new Set(start.ids));

  const employees = useMemo<ExportEmployee[]>(() => {
    const departmentNames = new Map(departments.map((d) => [d.id, d.name]));
    return payslips.map((p) => ({
      id: p.id,
      name: p.user?.fullName ?? '',
      staffNumber: p.user?.staffNumber ?? '',
      department: p.user?.department?.name ?? departmentNames.get(p.user?.departmentId ?? 0) ?? '',
    }));
  }, [payslips, departments]);

  const startNote = start.kind === 'ticked'
    ? t('payrollExport.employees.startTicked', { count: start.ids.length })
    : start.ids.length === payslips.length
      ? t('payrollExport.employees.startEveryone')
      : start.ids.length === 0
        ? t('payrollExport.employees.startNoMatch')
        : t('payrollExport.employees.startFiltered', { count: start.ids.length });

  const movedFromStart = selected.size !== start.ids.length || start.ids.some((id) => !selected.has(id));

  const unconvertedCount = useMemo(
    () => payslips.filter((p) => p.currencyRateUnavailable && selected.has(p.id)).length,
    [payslips, selected],
  );

  const blockedReason = selected.size === 0
    ? t('payrollExport.blocked.noEmployees')
    : needsGrouping(options) ? t('payrollExport.blocked.needsGroup') : null;

  const download = async () => {
    if (blockedReason || busy) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await DownloadPayrollTableExport(buildExportRequest({
        options,
        payslipIds: Array.from(selected),
        year,
        month,
        currencyId,
        periodText,
        culture: i18n.language,
        rtl: i18n.dir() === 'rtl',
        t,
      }));
      const fileName = exportFileName(year, month, options.layout);
      saveBlob(blob, fileName);
      saveOptions(options);
      setBusy(false);
      onDownloaded(fileName);
    } catch (e) {
      // Kept on screen: the panel stays open with the choices intact, so the reason can be acted on.
      setError(getApiErrorMessage(e, t, t('reports.exportFailed')));
      setBusy(false);
    }
  };

  return (
    <>
      {/* One disabled fieldset is what locks every control together while the file is being made. */}
      <fieldset disabled={busy} className="m-0 flex min-h-0 min-w-0 flex-1 flex-col border-0 p-0">
        <div className="min-h-0 flex-1 overflow-y-auto lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden">
          <div className="divide-y divide-border lg:overflow-y-auto">
            <ColumnsSection
              columns={options.columns}
              access={access}
              onChange={(columns) => setOptions((o) => ({ ...o, columns }))}
            />
            <LayoutSection options={options} onChange={(patch) => setOptions((o) => ({ ...o, ...patch }))} />
          </div>
          <div className="flex min-h-0 flex-col border-t border-border lg:border-s lg:border-t-0">
            <EmployeePicker
              employees={employees}
              selected={selected}
              onChange={setSelected}
              startNote={startNote}
              onResetToStart={movedFromStart ? () => setSelected(new Set(start.ids)) : undefined}
            />
            {unconvertedCount > 0 && (
              <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground" data-testid="payroll-export-unconverted">
                {t('payrollExport.employees.unconverted', { count: unconvertedCount })}
              </p>
            )}
          </div>
        </div>
      </fieldset>

      {error && (
        <div className="flex items-start gap-3 border-t border-destructive/30 bg-destructive/5 px-5 py-3" role="alert" data-testid="payroll-export-error">
          <AlertTriangle className="mt-0.5 shrink-0 text-destructive" size={16} aria-hidden="true" />
          <p className="min-w-0 text-sm text-foreground">{error}</p>
        </div>
      )}

      <div className="flex flex-col gap-3 border-t border-border p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <p
          className={blockedReason ? 'min-w-0 text-sm font-medium text-amber-700 dark:text-amber-300' : 'min-w-0 text-sm tabular-nums text-muted-foreground lg:invisible'}
          data-testid="payroll-export-footer-note"
        >
          {blockedReason ?? t('payrollExport.employees.count', { selected: selected.size, count: employees.length })}
        </p>
        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={onCancel}>{t('Cancel')}</Button>
          <Button
            iconName={busy ? null : 'Download'}
            loading={busy}
            disabled={!!blockedReason || busy}
            onClick={download}
            data-testid="payroll-export-download"
          >
            {busy ? t('payrollExport.downloading') : t('payrollExport.download')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default PayrollExportForm;
